<template>
  <div class="page-content" :class="page.cls">

    <!-- ==================== 目录 ==================== -->
    <div v-if="page.type === 'toc'" class="toc-page">
      <div class="toc-head">
        <span class="toc-kicker">CONTENTS</span>
        <h2 class="toc-title">目 录</h2>
        <div class="toc-rule"><span class="line"></span><span class="star">✦</span><span class="line"></span></div>
      </div>
      <div class="toc-list">
        <div v-for="(item, i) in tocItems" :key="i" class="toc-item" @click.stop="$emit('jump', item.page)">
          <span class="toc-no">{{ item.no }}</span>
          <span class="toc-name">{{ item.name }}</span>
          <span class="toc-leader"></span>
          <span class="toc-page">{{ item.label }}</span>
        </div>
      </div>
      <div class="toc-note">✦ 轻触条目 · 书页便会为你翻动 ✦</div>
      <svg class="toc-sigil" viewBox="0 0 200 200" aria-hidden="true">
        <circle cx="100" cy="100" r="72" fill="none" stroke="#b98a2f" stroke-width="1.4" stroke-dasharray="5 9" opacity=".6" />
        <circle cx="100" cy="100" r="50" fill="none" stroke="#c9a23c" stroke-width="1" opacity=".5" />
        <polygon points="100,58 124.3,133.6 56.1,90.2 143.9,90.2 75.7,133.6" fill="none" stroke="#c9a23c" stroke-width="1.2" opacity=".6" />
      </svg>
      <div class="page-no">— 壹 —</div>
    </div>

    <!-- ==================== 序章 ==================== -->
    <div v-else-if="page.type === 'preface'" class="pre-page">
      <span class="pre-kicker">序 章</span>
      <h2 class="pre-title">风的邀请</h2>
      <div class="pre-rule"><span class="line"></span><span>✧</span><span class="line"></span></div>
      <div class="pre-body">
        <p>致翻开这本书的你——</p>
        <p>今夜，风把星光吹散成了页码，时钟的指针在纸间悄悄倒转。</p>
        <p>我听见你的脚步停在扉页，于是魔法，便有了第一次呼吸。</p>
        <p>若你愿意，请顺着这一页页手账走下去——</p>
        <p>星屑会记得你的名字，云朵会替你收好秘密，而茶，永远不会凉。</p>
      </div>
      <svg class="pre-circle" viewBox="0 0 200 200" aria-hidden="true">
        <circle cx="100" cy="100" r="78" fill="none" stroke="#b98a2f" stroke-width="1.3" stroke-dasharray="6 10" opacity=".65" />
        <circle cx="100" cy="100" r="54" fill="none" stroke="#c9a23c" stroke-width="1" stroke-dasharray="30 8" opacity=".55" />
        <polygon points="100,62 122.7,133.1 59,92.6 141,92.6 77.3,133.1" fill="none" stroke="#c9a23c" stroke-width="1.1" opacity=".6" />
      </svg>
      <div class="pre-flower">
        <svg viewBox="0 0 40 64" aria-hidden="true">
          <path d="M20 60 C18 44 20 32 20 24" stroke="#8a6a34" stroke-width="1.6" fill="none" />
          <path d="M20 30 C13 26 9 20 10 14 C15 14 19 18 20 24" fill="#c9a876" opacity=".8" />
          <path d="M20 30 C27 26 31 20 30 14 C25 14 21 18 20 24" fill="#bf9c68" opacity=".8" />
          <path d="M20 26 C17 20 16 14 18 8 C22 10 22 16 21 22" fill="#d4b98c" opacity=".75" />
          <circle cx="20" cy="8" r="2.4" fill="#a37c40" opacity=".85" />
          <circle cx="11" cy="15" r="1.2" fill="#8a6a34" opacity=".6" />
          <circle cx="29" cy="15" r="1.2" fill="#8a6a34" opacity=".6" />
        </svg>
      </div>
      <span class="pre-scribble">wish you a gentle night ✦</span>
      <div class="page-no">— 贰 —</div>
    </div>

    <!-- ==================== 章节开篇 ==================== -->
    <div v-else-if="page.type === 'chapter'" class="ch-page">
      <div class="ch-frame"></div>
      <span class="ch-corner tl"></span>
      <span class="ch-corner tr"></span>
      <span class="ch-corner bl"></span>
      <span class="ch-corner br"></span>

      <div class="ch-top">
        <span class="ch-no">{{ page.no }}</span>
        <h2 class="ch-title">{{ page.title }}</h2>
        <div class="ch-sub">{{ page.sub }}</div>
        <div class="ch-rule">
          <span class="r-line"></span>
          <svg class="r-flourish" viewBox="0 0 40 16" aria-hidden="true">
            <path d="M20 8 C12 2 4 4 2 8 C4 12 12 14 20 8 Z" fill="none" stroke="#b98a2f" stroke-width="1" />
            <path d="M20 8 C28 2 36 4 38 8 C36 12 28 14 20 8 Z" fill="none" stroke="#b98a2f" stroke-width="1" />
            <path d="M20 4v8M17 6l3 2 3-2" stroke="#b98a2f" stroke-width="1" fill="none" />
          </svg>
          <span class="r-line"></span>
        </div>
      </div>

      <div class="ch-arch">
        <span class="ch-arch-glow"></span>
        <span class="ch-arch-inner">
          <img :src="page.silhouette" :alt="page.title + '魔法少女剪影'" class="ch-sil" @click.stop="$emit('preview', page.silhouette)" />
        </span>
      </div>

      <div class="ch-attr">
        <span class="ch-attr-star">✦</span>
        <span>魔法属性 · {{ page.attr }}</span>
        <span class="ch-attr-star">✦</span>
      </div>

      <div class="ch-poem">
        <span v-for="(line, i) in page.poem" :key="i" class="ch-line">{{ line }}</span>
      </div>

      <span class="ch-sigil"></span>
      <div class="page-no">— {{ page.num }} —</div>
    </div>

    <!-- ==================== 手账页 ==================== -->
    <div v-else-if="page.type === 'journal'" class="j-page">
      <div class="j-head">
        <span class="j-kicker">{{ page.kicker }}</span>
        <div class="j-title-wrap">
          <span class="j-title">{{ page.title }}</span>
          <span class="j-ribbon"></span>
        </div>
      </div>

      <div class="j-scene" :class="'fx-' + page.sceneName">
        <img :src="page.scene" :alt="page.title" class="j-scene-img" @click.stop="$emit('preview', page.scene)" />
        <div class="fx-layer">
          <template v-if="page.sceneName === 'snow'">
            <span v-for="(d, i) in snowDots" :key="'sd' + i" class="fx-snow" :style="d"></span>
          </template>
          <template v-else-if="page.sceneName === 'clock'">
            <span v-for="(c, i) in cloudBlobs" :key="'cb' + i" class="fx-cloud" :style="c"></span>
          </template>
          <template v-else-if="page.sceneName === 'tea'">
            <span v-for="(w, i) in steamWisps" :key="'sw' + i" class="fx-steam" :style="w"></span>
          </template>
          <template v-else-if="page.sceneName === 'globe'">
            <span v-for="(k, i) in sparkles" :key="'sp' + i" class="fx-spark" :style="k"></span>
          </template>
        </div>
        <span class="fx-shine"></span>
      </div>

      <div class="j-text">
        <p v-for="(t, i) in page.paras" :key="i">{{ t }}</p>
      </div>

      <!-- 主题小物：齿轮 / 冻结玫瑰 / 使魔 / 雪花 -->
      <span class="j-motto" :class="'motto-' + page.decor">
        <svg v-if="page.decor === 'gear'" class="motto-svg spin-slow" viewBox="0 0 48 48" aria-hidden="true">
          <g stroke="#8a6a2f" stroke-width="3" stroke-linecap="round">
            <line x1="24" y1="4" x2="24" y2="10" /><line x1="24" y1="38" x2="24" y2="44" />
            <line x1="4" y1="24" x2="10" y2="24" /><line x1="38" y1="24" x2="44" y2="24" />
            <line x1="9.9" y1="9.9" x2="14.1" y2="14.1" /><line x1="33.9" y1="33.9" x2="38.1" y2="38.1" />
            <line x1="9.9" y1="38.1" x2="14.1" y2="33.9" /><line x1="33.9" y1="14.1" x2="38.1" y2="9.9" />
          </g>
          <circle cx="24" cy="24" r="9.5" fill="none" stroke="#8a6a2f" stroke-width="2.2" />
          <circle cx="24" cy="24" r="3.2" fill="#8a6a2f" />
        </svg>
        <svg v-else-if="page.decor === 'rose'" class="motto-svg" viewBox="0 0 48 64" aria-hidden="true">
          <path d="M24 60 C22 46 24 34 24 26" stroke="#6d8a9e" stroke-width="1.8" fill="none" />
          <path d="M24 30 C18 24 14 18 16 10 C20 12 22 18 23 24 C25 18 29 13 34 11 C31 18 28 24 25 30" fill="#c9cdd8" stroke="#9fb4c4" stroke-width=".8" />
          <path d="M24 30 C24 22 26 14 32 10 C30 18 27 26 24 30 Z" fill="#b8c6d4" opacity=".9" />
          <path d="M18 26 l-6 -4 M30 24 l6 -6" stroke="#9fb4c4" stroke-width="1.4" stroke-linecap="round" />
          <path d="M20 36 l-5 2 M28 40 l6 -1" stroke="#cfe0ea" stroke-width="1.2" stroke-linecap="round" opacity=".8" />
        </svg>
        <svg v-else-if="page.decor === 'familiar'" class="motto-svg bob" viewBox="0 0 56 56" aria-hidden="true">
          <path d="M14 30 C14 18 22 12 28 12 C34 12 42 18 42 30 C42 40 36 46 28 46 C20 46 14 40 14 30 Z" fill="#e8b98a" />
          <path d="M18 18 l-5 -7 M38 18 l5 -7" stroke="#e8b98a" stroke-width="4" stroke-linecap="round" fill="none" />
          <path d="M23 27 a2 2 0 1 0 0.01 0 M33 27 a2 2 0 1 0 0.01 0" fill="#3d2a18" />
          <path d="M25 34 q3 3 6 0" stroke="#3d2a18" stroke-width="1.6" fill="none" stroke-linecap="round" />
          <circle cx="14" cy="40" r="6" fill="#f7e3c4" stroke="#d9a878" stroke-width="1" />
          <path d="M11 41 h6 M14 38 v6" stroke="#d9a878" stroke-width="1.4" stroke-linecap="round" />
          <circle cx="13" cy="39.4" r="1" fill="#e0556e" />
          <path d="M34 24 l4 -3 M36 20 l-3 4" stroke="#3d2a18" stroke-width="1" stroke-linecap="round" opacity=".35" />
        </svg>
        <svg v-else-if="page.decor === 'snowstar'" class="motto-svg twinkle" viewBox="0 0 48 48" aria-hidden="true">
          <g stroke="#8fb6c9" stroke-width="2" stroke-linecap="round">
            <line x1="24" y1="6" x2="24" y2="42" /><line x1="6" y1="24" x2="42" y2="24" />
            <line x1="11.3" y1="11.3" x2="36.7" y2="36.7" /><line x1="11.3" y1="36.7" x2="36.7" y2="11.3" />
          </g>
          <g stroke="#cfe0ea" stroke-width="1.6" stroke-linecap="round">
            <line x1="24" y1="13" x2="24" y2="19" /><line x1="24" y1="29" x2="24" y2="35" />
            <line x1="13" y1="24" x2="19" y2="24" /><line x1="29" y1="24" x2="35" y2="24" />
          </g>
        </svg>
      </span>

      <!-- 旧邮票 -->
      <span class="j-stamp">
        <span class="stamp-inner">
          <span class="stamp-title">{{ page.stamp }}</span>
          <span class="stamp-sub">{{ page.stampSub }}</span>
        </span>
        <span class="stamp-ring"></span>
      </span>

      <!-- 干花标本 -->
      <span class="j-flower">
        <svg viewBox="0 0 40 64" aria-hidden="true">
          <path d="M20 60 C18 44 20 32 20 24" stroke="#8a6a34" stroke-width="1.5" fill="none" />
          <path d="M20 30 C13 26 9 20 10 14 C15 14 19 18 20 24" fill="#c9a876" opacity=".75" />
          <path d="M20 30 C27 26 31 20 30 14 C25 14 21 18 20 24" fill="#bf9c68" opacity=".75" />
          <circle cx="20" cy="8" r="2.2" fill="#a37c40" opacity=".8" />
          <circle cx="11" cy="15" r="1.1" fill="#8a6a34" opacity=".55" />
          <circle cx="29" cy="15" r="1.1" fill="#8a6a34" opacity=".55" />
        </svg>
      </span>

      <!-- 手绘涂鸦 -->
      <svg class="j-doodle" viewBox="0 0 90 40" aria-hidden="true">
        <path d="M8 24 l6 -10 l6 10 M20 24 l4 -7 l4 7 M36 20 c4 -6 8 -6 12 0 c-4 6 -8 6 -12 0 Z M56 26 l5 -9 l5 9" stroke="#8a6a34" stroke-width="1.2" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity=".7" />
        <path d="M72 12 c3 -5 7 -5 10 0 c-3 5 -7 5 -10 0 Z M78 22 l3 -4 l3 4" stroke="#8a6a34" stroke-width="1.1" fill="none" stroke-linecap="round" opacity=".6" />
      </svg>

      <!-- 页角折角 -->
      <span class="j-fold"></span>
      <div class="page-no">— {{ page.num }} —</div>
    </div>

    <!-- ==================== 终章 ==================== -->
    <div v-else-if="page.type === 'ending'" class="end-page">
      <svg class="end-bg-circle" viewBox="0 0 200 200" aria-hidden="true">
        <circle cx="100" cy="100" r="88" fill="none" stroke="#c9a23c" stroke-width="1.2" stroke-dasharray="6 10" opacity=".4" />
        <circle cx="100" cy="100" r="64" fill="none" stroke="#c9a23c" stroke-width="1" stroke-dasharray="2 6" opacity=".35" />
        <polygon points="100,52 128.6,146.2 52.8,88.9 147.2,88.9 71.4,146.2" fill="none" stroke="#c9a23c" stroke-width="1" opacity=".4" />
      </svg>
      <span class="end-no">终</span>
      <h2 class="end-title">立绘的苏醒</h2>
      <div class="end-rule"><span class="line"></span><span>❀</span><span class="line"></span></div>
      <div class="end-body">
        <p v-for="(line, i) in page.lines" :key="i">{{ line }}</p>
      </div>
      <button class="awaken-btn" @click.stop="$emit('awaken')">
        <svg class="ab-star" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" fill="currentColor" />
        </svg>
        <span>立绘 · 苏醒</span>
        <svg class="ab-star" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" fill="currentColor" />
        </svg>
      </button>
      <div class="end-hint">樱花盛开之处，有两位少女在等你</div>
      <span class="end-corner tl"></span>
      <span class="end-corner br"></span>
      <div class="page-no">— 拾壹 —</div>
    </div>

    <!-- ==================== 尾页 ==================== -->
    <div v-else-if="page.type === 'colophon'" class="col-page">
      <svg class="col-bg-circle" viewBox="0 0 200 200" aria-hidden="true">
        <circle cx="100" cy="100" r="82" fill="none" stroke="#b98a2f" stroke-width="1.1" stroke-dasharray="5 9" opacity=".4" />
        <circle cx="100" cy="100" r="56" fill="none" stroke="#c9a23c" stroke-width="1" stroke-dasharray="26 9" opacity=".35" />
        <polygon points="100,60 127.6,134.4 57.6,92 142.4,92 72.4,134.4" fill="none" stroke="#c9a23c" stroke-width="1" opacity=".4" />
      </svg>
      <span class="col-kicker">EPILOGUE</span>
      <h2 class="col-title">{{ page.title }}</h2>
      <div class="col-rule"><span class="line"></span><span>✧</span><span class="line"></span></div>
      <div class="col-sub">{{ page.sub }}</div>
      <div class="col-body">
        <p v-for="(line, i) in page.lines" :key="i">{{ line }}</p>
      </div>
      <div class="col-seal">{{ page.stamp }}</div>
      <span class="col-star">✦</span>
      <div class="page-no">— 拾贰 —</div>
    </div>

  </div>
</template>

<script>
function rand (min, max) {
  return (min + Math.random() * (max - min)).toFixed(1)
}

export default {
  name: 'PageContent',
  props: {
    page: { type: Object, required: true }
  },
  data () {
    return {
      tocItems: [
        { no: '序', name: '风的邀请', label: '一', page: 1 },
        { no: '壹', name: '星之章 · 夜空的纺织者', label: '二', page: 2 },
        { no: '贰', name: '月之章 · 时间的守钟人', label: '三', page: 4 },
        { no: '叁', name: '花之章 · 茶会与铃兰', label: '四', page: 6 },
        { no: '肆', name: '雪之章 · 永不融化的冬天', label: '五', page: 8 },
        { no: '终', name: '立绘的苏醒', label: '✦', page: 10 }
      ],
      snowDots: Array.from({ length: 14 }, () => ({
        left: rand(0, 94) + '%',
        top: rand(-10, 20) + '%',
        width: rand(2, 5) + 'px',
        height: rand(2, 5) + 'px',
        animationDelay: rand(0, 4) + 's',
        animationDuration: rand(2.6, 5.5) + 's',
        opacity: rand(0.35, 0.9)
      })),
      cloudBlobs: [
        { top: '16%', left: '-26%', '--s': '1', animationDuration: '26s', animationDelay: '-4s' },
        { top: '46%', left: '-40%', '--s': '0.72', animationDuration: '34s', animationDelay: '-16s' },
        { top: '68%', left: '-30%', '--s': '0.5', animationDuration: '29s', animationDelay: '-26s' }
      ],
      steamWisps: [
        { left: '30%', bottom: '38%', '--s': '1', animationDuration: '3.1s', animationDelay: '0s' },
        { left: '50%', bottom: '36%', '--s': '0.8', animationDuration: '3.8s', animationDelay: '0.9s' },
        { left: '66%', bottom: '40%', '--s': '1.1', animationDuration: '3.4s', animationDelay: '1.7s' }
      ],
      sparkles: Array.from({ length: 6 }, () => ({
        left: rand(8, 88) + '%',
        top: rand(12, 84) + '%',
        size: rand(2.5, 5) + 'px',
        animationDelay: rand(0, 3) + 's',
        animationDuration: rand(1.8, 3.6) + 's'
      }))
    }
  }
}
</script>

<style scoped>
/* ================= 通用 ================= */
.page-content {
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  padding: 30px 36px 30px 38px;
  font-size: clamp(13px, 1.42vh, 15.5px);
  color: var(--ink);
  background:
    linear-gradient(148deg, #f6ecd3 0%, #efe0bd 46%, #e7d4a9 100%);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  contain: layout paint;
}
.page-content > [class$='-page'] {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
}
.page-content::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/><feColorMatrix values='0 0 0 0 0.5 0 0 0 0 0.4 0 0 0 0 0.24 0 0 0 0.05 0'/></filter><rect width='120' height='120' filter='url(%23n)'/></svg>");
  opacity: 0.35;
  pointer-events: none;
}
.page-content::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 24% 18%, rgba(255, 255, 255, 0.5) 1px, transparent 1.6px),
    radial-gradient(circle at 70% 40%, rgba(255, 255, 255, 0.4) 1px, transparent 1.6px),
    radial-gradient(circle at 50% 76%, rgba(160, 120, 50, 0.12) 1px, transparent 1.8px);
  background-size: 90px 90px, 130px 130px, 110px 110px;
  opacity: 0.55;
  pointer-events: none;
}
.page-no {
  position: absolute;
  bottom: 12px;
  left: 0;
  right: 0;
  text-align: center;
  font-family: var(--font-star);
  font-size: 0.78em;
  letter-spacing: 0.32em;
  color: rgba(120, 88, 40, 0.6);
}

/* ================= 目录 ================= */
.toc-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 34px;
  min-height: 0;
}
.toc-head {
  text-align: center;
  position: relative;
}
.toc-kicker {
  font-family: Georgia, serif;
  font-size: 0.62em;
  letter-spacing: 0.52em;
  color: #a37c40;
}
.toc-title {
  font-family: var(--font-brush);
  font-weight: normal;
  font-size: 2.6em;
  letter-spacing: 0.34em;
  margin-top: 2px;
  color: #6b4a1e;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.6);
}
.toc-rule {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 74%;
  margin: 8px auto 0;
}
.toc-rule .line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #b98a2f, transparent);
}
.toc-rule .star {
  color: #b98a2f;
  font-size: 0.85em;
}
.toc-list {
  width: 82%;
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.toc-item {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 7px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.toc-item:hover {
  background: linear-gradient(90deg, rgba(185, 138, 47, 0.14), rgba(185, 138, 47, 0.03));
  transform: translateX(5px);
}
.toc-no {
  font-family: var(--font-brush);
  font-size: 1.25em;
  color: #9a7420;
  min-width: 1.7em;
  text-align: center;
}
.toc-name {
  font-family: var(--font-star);
  font-size: 1.02em;
  color: var(--ink);
  letter-spacing: 0.06em;
}
.toc-leader {
  flex: 1;
  border-bottom: 1px dotted rgba(120, 88, 40, 0.45);
  transform: translateY(-4px);
}
.toc-page {
  font-family: var(--font-star);
  font-size: 0.95em;
  color: #a37c40;
}
.toc-note {
  margin-top: auto;
  margin-bottom: 26px;
  font-family: var(--font-star);
  font-size: 0.82em;
  letter-spacing: 0.14em;
  color: rgba(120, 88, 40, 0.72);
}
.toc-sigil {
  position: absolute;
  right: 22px;
  top: 26px;
  width: 74px;
  opacity: 0.75;
  animation: sigil-spin 46s linear infinite;
}
@keyframes sigil-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ================= 序章 ================= */
.pre-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-top: 30px;
  justify-content: flex-start;
}
.pre-kicker {
  font-family: var(--font-brush);
  font-size: 1.35em;
  letter-spacing: 0.6em;
  color: #9a7420;
}
.pre-title {
  font-family: var(--font-brush);
  font-weight: normal;
  font-size: 2.5em;
  letter-spacing: 0.18em;
  margin-top: 6px;
  color: #6b4a1e;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.6);
}
.pre-rule {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 56%;
  margin-top: 8px;
  color: #b98a2f;
  font-size: 0.85em;
}
.pre-rule .line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #b98a2f, transparent);
}
.pre-body {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 11px;
  max-width: 88%;
  width: 100%;
  flex: 1 1 auto;
}
.pre-body p {
  font-family: var(--font-kx);
  font-size: 1.02em;
  line-height: 1.72;
  letter-spacing: 0.04em;
  text-align: justify;
  text-align-last: center;
}
.pre-body p:first-child {
  font-family: var(--font-brush);
  font-size: 1.3em;
  color: #7a5a28;
}
.pre-circle {
  position: absolute;
  left: 20px;
  bottom: 30px;
  width: 84px;
  opacity: 0.8;
  animation: sigil-spin 52s linear infinite;
}
.pre-flower {
  position: absolute;
  right: 18px;
  bottom: 22px;
  width: 46px;
  opacity: 0.85;
  transform: rotate(-8deg);
  animation: float-soft 5s ease-in-out infinite;
}
.pre-scribble {
  position: absolute;
  right: 30px;
  top: 24px;
  font-family: Georgia, serif;
  font-style: italic;
  font-size: 0.72em;
  letter-spacing: 0.1em;
  color: rgba(120, 88, 40, 0.55);
  transform: rotate(3deg);
}

/* ================= 章节页 ================= */
.ch-page {
  background:
    linear-gradient(150deg, #f3e8cd 0%, #ead9b4 55%, #e2cea4 100%);
  padding: 30px 34px 26px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}
.ch-frame {
  position: absolute;
  inset: 14px;
  border: 1px solid rgba(185, 138, 47, 0.6);
  border-radius: 4px;
  box-shadow: inset 0 0 0 2px rgba(185, 138, 47, 0.08), inset 0 0 20px rgba(120, 88, 40, 0.1);
  pointer-events: none;
}
.ch-corner {
  position: absolute;
  width: 22px;
  height: 22px;
  border: 2px solid #b98a2f;
  pointer-events: none;
}
.ch-corner.tl { top: 18px; left: 18px; border-right: none; border-bottom: none; border-radius: 4px 0 0 0; }
.ch-corner.tr { top: 18px; right: 18px; border-left: none; border-bottom: none; border-radius: 0 4px 0 0; }
.ch-corner.bl { bottom: 18px; left: 18px; border-right: none; border-top: none; border-radius: 0 0 0 4px; }
.ch-corner.br { bottom: 18px; right: 18px; border-left: none; border-top: none; border-radius: 0 0 4px 0; }

.ch-top {
  text-align: center;
  margin-top: 8px;
}
.ch-no {
  display: block;
  font-family: var(--font-brush);
  font-size: 1.7em;
  letter-spacing: 0.4em;
  color: #b98a2f;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.55);
}
.ch-title {
  font-family: var(--font-brush);
  font-weight: normal;
  font-size: 2.35em;
  letter-spacing: 0.2em;
  margin-top: 1px;
  background: linear-gradient(180deg, #8a5f1e, #c9a23c 60%, #8a5f1e);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 1px 0 rgba(255, 255, 255, 0.55));
}
.ch-sub {
  font-family: var(--font-star);
  font-size: 0.95em;
  letter-spacing: 0.3em;
  color: #7a5a28;
  margin-top: 3px;
}
.ch-rule {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 64%;
  margin: 10px auto 0;
}
.ch-rule .r-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #b98a2f, transparent);
}
.r-flourish {
  width: 30px;
  height: 12px;
}

/* 立绘剪影拱窗 */
.ch-arch {
  position: relative;
  width: 46%;
  aspect-ratio: 4 / 5;
  margin: 16px auto 0;
}
.ch-arch-glow {
  position: absolute;
  inset: 8%;
  border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(255, 224, 140, 0.5), transparent 68%);
  animation: window-pulse 3.6s ease-in-out infinite;
}
.ch-arch-inner {
  position: absolute;
  inset: 0;
  border-radius: 999px 999px 12px 12px;
  overflow: hidden;
  border: 2px solid rgba(185, 138, 47, 0.75);
  background: linear-gradient(180deg, #fbf3dd, #efe0bd);
  box-shadow: inset 0 0 0 3px rgba(255, 255, 255, 0.5), 0 8px 22px rgba(120, 88, 40, 0.28);
}
.ch-sil {
  cursor: zoom-in;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  mix-blend-mode: multiply;
  filter: sepia(0.14) contrast(1.06);
}
.ch-attr {
  margin-top: 14px;
  text-align: center;
  font-family: var(--font-star);
  font-size: 0.9em;
  letter-spacing: 0.16em;
  color: #7a5a28;
}
.ch-attr-star {
  margin: 0 8px;
  color: #b98a2f;
  font-size: 0.8em;
}
.ch-poem {
  margin-top: 13px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  text-align: center;
}
.ch-line {
  font-family: var(--font-kx);
  font-size: 0.98em;
  line-height: 1.6;
  letter-spacing: 0.05em;
  color: var(--ink-soft);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
}
.ch-line:first-child {
  font-family: var(--font-brush);
  font-size: 1.16em;
  color: #7a5a28;
}
.ch-sigil {
  position: absolute;
  right: 30px;
  top: 30px;
  width: 26px;
  height: 26px;
  background: radial-gradient(circle, #fff3cf 0%, rgba(255, 227, 150, 0.6) 40%, transparent 72%);
  border-radius: 50%;
  animation: window-pulse 2.8s ease-in-out infinite;
}

/* ================= 手账页 ================= */
.j-page {
  padding: 28px 34px 30px 36px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}
.j-head {
  margin-bottom: 12px;
}
.j-kicker {
  display: block;
  font-family: var(--font-star);
  font-size: 0.82em;
  letter-spacing: 0.34em;
  color: #a37c40;
  margin-bottom: 4px;
}
.j-title-wrap {
  position: relative;
  display: inline-block;
}
.j-title {
  font-family: var(--font-brush);
  font-size: 1.85em;
  letter-spacing: 0.12em;
  color: #6b4a1e;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.6);
}
.j-ribbon {
  position: absolute;
  left: -8px;
  right: -8px;
  bottom: -5px;
  height: 9px;
  background: linear-gradient(90deg, rgba(214, 162, 90, 0.32), rgba(214, 162, 90, 0.12));
  clip-path: polygon(0 0, calc(100% - 9px) 0, 100% 50%, calc(100% - 9px) 100%, 0 100%, 9px 50%);
  opacity: 0.85;
}

/* 微缩场景（圆形，文字环绕） */
.j-scene {
  position: relative;
  float: right;
  width: 44%;
  aspect-ratio: 1;
  border-radius: 50%;
  overflow: hidden;
  margin: 4px 0 16px 20px;
  shape-outside: circle(50%);
  background: #fff;
  border: 3px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 10px 26px rgba(120, 88, 40, 0.3), inset 0 0 0 1px rgba(160, 120, 50, 0.35);
}
.j-scene-img {
  cursor: zoom-in;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.fx-layer {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 50%;
  pointer-events: none;
}
.fx-shine {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(115deg, rgba(255, 255, 255, 0.25) 0%, transparent 30%);
  animation: shine-sweep 6s ease-in-out infinite;
}
@keyframes shine-sweep {
  0%, 100% { transform: translateX(-30%); opacity: 0.35; }
  50% { transform: translateX(30%); opacity: 0.7; }
}

/* 细雪 */
.fx-snow {
  position: absolute;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 6px 1px rgba(255, 255, 255, 0.7);
  animation: snow-fall linear infinite;
}
@keyframes snow-fall {
  0% { transform: translateY(-6%) translateX(0); opacity: 0; }
  12% { opacity: 1; }
  100% { transform: translateY(560%) translateX(34px); opacity: 0.6; }
}

/* 云朵漂移 */
.fx-cloud {
  position: absolute;
  width: 74%;
  height: 30%;
  border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 0.2) 65%, transparent 75%);
  filter: blur(5px);
  animation: cloud-drift linear infinite;
}
@keyframes cloud-drift {
  from { transform: translateX(-70%) scale(var(--s, 1)); opacity: 0; }
  12% { opacity: 0.9; }
  88% { opacity: 0.9; }
  to { transform: translateX(200%) scale(var(--s, 1)); opacity: 0; }
}

/* 红茶蒸汽 */
.fx-steam {
  position: absolute;
  width: 16%;
  height: 46%;
  border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.85), transparent 70%);
  filter: blur(6px);
  animation: steam-rise ease-in-out infinite;
}
@keyframes steam-rise {
  0% { transform: translateY(0) scale(var(--s, 1)); opacity: 0; }
  30% { opacity: 0.75; }
  100% { transform: translateY(-120%) translateX(6px) scale(calc(var(--s, 1) * 1.4)); opacity: 0; }
}

/* 雪晶球星光 */
.fx-spark {
  position: absolute;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 7px 2px rgba(255, 240, 200, 0.85);
  animation: spark-twinkle ease-in-out infinite;
}
@keyframes spark-twinkle {
  0%, 100% { opacity: 0.15; transform: scale(0.7); }
  50% { opacity: 1; transform: scale(1.25); }
}

/* 手账正文（环绕图片） */
.j-text {
  margin-top: 6px;
  min-height: 0;
}
.j-text p {
  font-family: var(--font-kx);
  font-size: 1em;
  line-height: 1.78;
  letter-spacing: 0.04em;
  text-align: justify;
  margin: 0 0 10px;
  color: var(--ink);
  max-width: 100%;
  word-break: break-word;
}
.j-text p:first-child::first-letter {
  font-family: var(--font-brush);
  font-size: 1.8em;
  color: #9a7420;
  float: left;
  margin: 2px 6px 0 0;
  line-height: 1.05;
}

/* 主题小物 */
.j-motto {
  position: absolute;
  right: 7%;
  top: 56%;
  width: 40px;
  opacity: 0.88;
  filter: drop-shadow(0 2px 4px rgba(120, 88, 40, 0.25));
}
.motto-svg {
  width: 100%;
  height: auto;
  display: block;
}
.spin-slow {
  animation: sigil-spin 14s linear infinite;
}
.bob {
  animation: bob-soft 3.2s ease-in-out infinite;
}
@keyframes bob-soft {
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-5px) rotate(2deg); }
}
.twinkle {
  animation: spark-twinkle 2.6s ease-in-out infinite;
}

/* 旧邮票 */
.j-stamp {
  position: absolute;
  left: 8%;
  bottom: 16%;
  width: 76px;
  padding: 9px 7px;
  background: #f6ecd4;
  border: 1px dashed rgba(120, 88, 40, 0.55);
  transform: rotate(-7deg);
  box-shadow: 0 3px 8px rgba(120, 88, 40, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  -webkit-mask-image: radial-gradient(circle 3px at 4px 4px, transparent 97%, #000), radial-gradient(circle 3px at calc(100% - 4px) 4px, transparent 97%, #000), radial-gradient(circle 3px at 4px calc(100% - 4px), transparent 97%, #000), radial-gradient(circle 3px at calc(100% - 4px) calc(100% - 4px), transparent 97%, #000);
  -webkit-mask-composite: source-in;
  mask-image: radial-gradient(circle 3px at 4px 4px, transparent 97%, #000), radial-gradient(circle 3px at calc(100% - 4px) 4px, transparent 97%, #000), radial-gradient(circle 3px at 4px calc(100% - 4px), transparent 97%, #000), radial-gradient(circle 3px at calc(100% - 4px) calc(100% - 4px), transparent 97%, #000);
  mask-composite: intersect;
}
.stamp-inner {
  border: 1px solid rgba(140, 100, 40, 0.7);
  padding: 5px 6px;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
}
.stamp-title {
  display: block;
  font-family: var(--font-brush);
  font-size: 0.98em;
  letter-spacing: 0.18em;
  color: #7a4a20;
}
.stamp-sub {
  display: block;
  font-family: var(--font-star);
  font-size: 0.62em;
  letter-spacing: 0.14em;
  color: rgba(110, 80, 40, 0.75);
  margin-top: 2px;
}
.stamp-ring {
  position: absolute;
  right: -8px;
  top: 26%;
  width: 30px;
  height: 30px;
  border: 1.5px solid rgba(140, 100, 40, 0.4);
  border-radius: 50%;
  transform: rotate(14deg);
  pointer-events: none;
}

/* 干花标本 */
.j-flower {
  position: absolute;
  left: 8%;
  bottom: 36%;
  width: 40px;
  opacity: 0.8;
  transform: rotate(12deg);
  animation: float-soft 6s ease-in-out infinite;
}
@keyframes float-soft {
  0%, 100% { transform: rotate(10deg) translateY(0); }
  50% { transform: rotate(14deg) translateY(-4px); }
}

/* 手绘涂鸦 */
.j-doodle {
  position: absolute;
  left: 6%;
  top: 26%;
  width: 92px;
  opacity: 0.6;
  animation: float-soft 7s ease-in-out infinite;
}

/* 页角折角 */
.j-fold {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 0 40px 40px;
  border-color: transparent transparent rgba(150, 110, 50, 0.16) transparent;
  pointer-events: none;
}
.j-fold::after {
  content: '';
  position: absolute;
  top: 22px;
  left: -30px;
  width: 40px;
  height: 40px;
  border-style: solid;
  border-width: 0 0 18px 18px;
  border-color: transparent transparent rgba(255, 252, 240, 0.75) transparent;
  transform: rotate(45deg);
}

/* ================= 终章 ================= */
.end-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-top: 34px;
  justify-content: flex-start;
}
.end-bg-circle {
  position: absolute;
  left: 50%;
  top: 46%;
  width: 96%;
  transform: translate(-50%, -50%);
  animation: sigil-spin 60s linear infinite;
  opacity: 0.9;
}
.end-no {
  font-family: var(--font-brush);
  font-size: 1.8em;
  letter-spacing: 0.5em;
  color: #b98a2f;
  position: relative;
}
.end-title {
  font-family: var(--font-brush);
  font-weight: normal;
  font-size: 2.3em;
  letter-spacing: 0.16em;
  margin-top: 4px;
  color: #6b4a1e;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.6);
  position: relative;
}
.end-rule {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 58%;
  margin-top: 8px;
  color: #b98a2f;
  font-size: 0.9em;
  position: relative;
}
.end-rule .line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #b98a2f, transparent);
}
.end-body {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  max-width: 86%;
  position: relative;
}
.end-body p {
  font-family: var(--font-kx);
  font-size: 1em;
  line-height: 1.66;
  letter-spacing: 0.05em;
}
.end-body p:first-child {
  font-family: var(--font-brush);
  font-size: 1.16em;
  color: #7a5a28;
}
.awaken-btn {
  margin-top: 26px;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 13px 30px;
  border-radius: 999px;
  border: 1px solid rgba(212, 175, 55, 0.85);
  background: linear-gradient(180deg, #fbe9b8, #e8c26a 55%, #caa03e);
  color: #5d3f12;
  font-family: var(--font-brush);
  font-size: 1.25em;
  letter-spacing: 0.22em;
  cursor: pointer;
  box-shadow: 0 0 24px rgba(233, 193, 100, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 6px 18px rgba(120, 88, 40, 0.35);
  animation: btn-pulse 2.4s ease-in-out infinite;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.awaken-btn:hover {
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 0 40px rgba(255, 214, 130, 0.85), inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 8px 24px rgba(120, 88, 40, 0.4);
}
@keyframes btn-pulse {
  0%, 100% { box-shadow: 0 0 20px rgba(233, 193, 100, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 6px 18px rgba(120, 88, 40, 0.35); }
  50% { box-shadow: 0 0 42px rgba(255, 214, 130, 0.85), inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 6px 18px rgba(120, 88, 40, 0.35); }
}
.ab-star {
  width: 15px;
  height: 15px;
  color: #8a6418;
  filter: drop-shadow(0 1px 1px rgba(255, 255, 255, 0.5));
}
.end-hint {
  margin-top: 14px;
  font-family: var(--font-star);
  font-size: 0.8em;
  letter-spacing: 0.2em;
  color: rgba(120, 88, 40, 0.72);
  position: relative;
}
.end-corner {
  position: absolute;
  width: 26px;
  height: 26px;
  border: 2px solid #b98a2f;
  opacity: 0.6;
}
.end-corner.tl { top: 18px; left: 18px; border-right: none; border-bottom: none; border-radius: 4px 0 0 0; }
.end-corner.br { bottom: 18px; right: 18px; border-left: none; border-top: none; border-radius: 0 0 4px 0; }

/* ================= 尾页 ================= */
.col-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding-top: 6px;
  min-height: 0;
}
.col-bg-circle {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 74%;
  transform: translate(-50%, -54%);
  opacity: 0.9;
}
.col-kicker {
  font-family: Georgia, serif;
  font-size: 0.62em;
  letter-spacing: 0.52em;
  color: #a37c40;
}
.col-title {
  font-family: var(--font-brush);
  font-weight: normal;
  font-size: 2.5em;
  letter-spacing: 0.3em;
  margin-top: 8px;
  color: #6b4a1e;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.6);
}
.col-rule {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 60%;
  margin: 12px auto 0;
}
.col-rule .line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #b98a2f, transparent);
}
.col-rule span {
  color: #b98a2f;
  font-size: 0.8em;
}
.col-sub {
  font-family: var(--font-star);
  font-size: 1.05em;
  letter-spacing: 0.3em;
  color: #8a6a34;
  margin-top: 16px;
}
.col-body {
  margin-top: 22px;
  width: 86%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.col-body p {
  margin: 0;
  line-height: 1.9;
  color: #6b543a;
}
.col-seal {
  margin-top: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 74px;
  height: 74px;
  border-radius: 50%;
  border: 1.5px solid rgba(212, 175, 55, 0.55);
  background: rgba(255, 251, 240, 0.35);
  color: #b98a2f;
  font-family: var(--font-brush);
  font-size: 1.5em;
  box-shadow: 0 0 16px rgba(212, 175, 55, 0.18);
}
.col-star {
  position: absolute;
  right: 30px;
  bottom: 52px;
  font-size: 1.1em;
  color: rgba(185, 138, 47, 0.5);
  animation: col-twinkle 3s ease-in-out infinite;
}
@keyframes col-twinkle {
  0%, 100% { opacity: 0.4; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1.15); }
}

/* ================= 动效通用 ================= */
@keyframes window-pulse {
  0%, 100% { opacity: 0.6; transform: scale(0.96); }
  50% { opacity: 1; transform: scale(1.04); }
}
</style>
